/**
 * Fan app routing: hub hosts (app/www/…) show a team picker; dedicated team subdomains skip it.
 * Keep subdomain fallbacks aligned with `services/game.ts` hostname normalization.
 */

/**
 * Shared Momento hosts (e.g. app.mymomento.com): welcome uses default/Momento branding until
 * /team/hostname/{prefix} returns a team; 404 → treat as multi-club hub (Continue → /teams).
 * A team row whose `hostname` matches `app`/`www`/… would instead get dedicated welcome assets.
 */
export const FAN_FIRST_PARTY_HOST_PREFIXES = new Set([
  "app",
  "dev",
  "www",
  "operator",
  // Regional base host: bare uk.mymomento.com is the hub, not a team named "uk".
  // ({team}.uk.mymomento.com still resolves the team from its first hostname label.)
  "uk",
  // CloudFront fan app hosts (no team subdomain) — hub / team-picker flow
  "mom-dev-client",
  "mom-prod-client",
]);

/** True when the first DNS label is a Momento app host, not a club hostname. */
export function isFanFirstPartyHostPrefix(prefix: string): boolean {
  return FAN_FIRST_PARTY_HOST_PREFIXES.has((prefix || "").toLowerCase());
}

/** When hostname API fails, map subdomain → normalized home team name (lowercase). */
export const FAN_DEDICATED_SUBDOMAIN_TEAM_NAME: Record<string, string> = {
  redbulls: "new york red bulls",
  lagalaxy: "los angeles galaxy",
  sportingkc: "sporting kansas city",
  lightning: "tampa bay lightning",
  kraken: "seattle kraken",
  seahawks: "seattle seahawks",
  mariners: "seattle mariners",
  gothamfc: "gotham fc",
  storm: "seattle storm",
};

export function getFanHostnamePrefix(hostname: string): string {
  const i = hostname.indexOf(".");
  return i === -1 ? hostname.toLowerCase() : hostname.substring(0, i).toLowerCase();
}

const PREVIEW_HOST_KEY = "momento_flow_preview_host";

/** Preview hostname from the URL or this tab, e.g. mariners-preview. */
export function getFlowPreviewHost(): string {
  if (typeof window === "undefined") return "";
  const fromQuery = new URLSearchParams(window.location.search).get("previewHost");
  if (fromQuery?.trim()) {
    const host = fromQuery.trim().toLowerCase();
    sessionStorage.setItem(PREVIEW_HOST_KEY, host);
    return host;
  }
  const stored = sessionStorage.getItem(PREVIEW_HOST_KEY);
  if (stored) return stored;
  const prefix = getFanHostnamePrefix(window.location.hostname);
  return prefix.endsWith("-preview") ? prefix : "";
}

/** Host label the API should treat as the fan site (live subdomain or preview). */
export function getRequestFanHost(): string {
  const preview = getFlowPreviewHost();
  if (preview) return preview;
  if (typeof window === "undefined") return "";
  return getFanHostnamePrefix(window.location.hostname);
}

export function isFanApexOrLocalhost(hostname: string): boolean {
  return hostname.split(".").filter(Boolean).length <= 2;
}

/** e.g. seattle.* — shared regional fan hostname; fans pick a club on /teams after welcome. */
export function isFanMultiTeamRegionHostname(hostname: string): boolean {
  return hostname.toLowerCase().startsWith("seattle.");
}

/** Hardcoded dedicated club subdomains — never send these hosts to /teams from /games. */
export function isFanKnownDedicatedSubdomainHostname(hostname: string): boolean {
  const prefix = getFanHostnamePrefix(hostname);
  return Boolean(FAN_DEDICATED_SUBDOMAIN_TEAM_NAME[prefix]);
}

/**
 * Best-effort sync guess before /team/hostname API resolves.
 * Unknown club subdomains default to dedicated flow (skip /teams); the hostname team
 * API sets `fanWelcomeRequiresClubPicker` when a host should behave as a multi-club hub.
 * First-party and known regional patterns (e.g. seattle.*) still default to hub here.
 */
export function computeFanFlowRequiresTeamPickerSync(hostname: string): boolean {
  if (isFanApexOrLocalhost(hostname)) return true;
  const prefix = getFanHostnamePrefix(hostname);
  if (isFanFirstPartyHostPrefix(prefix)) return true;
  if (FAN_DEDICATED_SUBDOMAIN_TEAM_NAME[prefix]) return false;
  if (isFanMultiTeamRegionHostname(hostname)) return true;
  return false;
}
